import apiClient from './apiService';

export interface Task {
    id: number; // Changed to number
    project_id: number; // Changed to number
    parent_id: number | null; // Changed to number
    wbs_code: string | null;
    name: string;
    description: string | null;
    is_milestone: boolean;
    duration_planned: number;
    effort_estimated: number;
    optimistic?: number | null;
    most_likely?: number | null;
    pessimistic?: number | null;
    constraint_type: 'ASAP' | 'ALAP' | 'SNET' | 'SNLT' | 'FNET' | 'FNLT' | 'MSO' | 'MFO';
    constraint_date: string | null;
    deadline_date: string | null;
    actual_start_date: string | null;
    actual_finish_date: string | null;
    percent_complete: number;
    effort_actual: number;
    early_start?: string;
    early_finish?: string;
    late_start?: string;
    late_finish?: string;
    total_slack?: number;
    is_critical?: boolean;
    pert_expected?: number | null;
    pert_variance?: number | null;
    pert_std_dev?: number | null;
    budgeted_resource_cost?: number;
    actual_resource_cost?: number;
    sequence_no?: number | null;
}

export interface TaskCreate {
    project_id: number; // Changed to number
    parent_id?: number | null; // Changed to number
    wbs_code?: string;
    name: string;
    description?: string;
    is_milestone?: boolean;
    duration_planned?: number;
    effort_estimated?: number;
    optimistic?: number | null;
    most_likely?: number | null;
    pessimistic?: number | null;
    constraint_type?: string;
    constraint_date?: string | null;
    deadline_date?: string | null;
    sequence_no?: number | null;
}

export interface TaskUpdate {
    project_id: number; // Changed to number
    parent_id?: number | null; // Changed to number
    wbs_code?: string | null;
    name?: string;
    description?: string | null;
    is_milestone?: boolean;
    duration_planned?: number;
    effort_estimated?: number;
    optimistic?: number | null;
    most_likely?: number | null;
    pessimistic?: number | null;
    constraint_type?: string;
    constraint_date?: string | null;
    deadline_date?: string | null;
    actual_start_date?: string | null;
    actual_finish_date?: string | null;
    percent_complete?: number;
    effort_actual?: number;
    sequence_no?: number | null;
}

export interface Dependency {
    id: number; // Changed to number
    predecessor_id: number; // Changed to number
    successor_id: number; // Changed to number
    type: 'FS' | 'SS' | 'FF' | 'SF';
    lag_days: number;
}

export interface DependencyCreate {
    predecessor_id: number; // Changed to number
    successor_id: number; // Changed to number
    type?: 'FS' | 'SS' | 'FF' | 'SF';
    lag_days?: number;
}

export const taskService = {
    getTasks: async (projectId: string | number): Promise<Task[]> => {
        const response = await apiClient.get(`/api/v1/prm/projects/${projectId}/tasks`);
        return response.data;
    },

    createTask: async (data: TaskCreate): Promise<Task> => {
        const response = await apiClient.post(`/api/v1/prm/projects/${data.project_id}/tasks`, data);
        return response.data;
    },

    updateTask: async (id: string | number, data: TaskUpdate): Promise<Task> => {
        const response = await apiClient.put(`/api/v1/prm/projects/${data.project_id}/tasks/${id}`, data);
        return response.data;
    },

    deleteTask: async (projectId: string | number, id: string | number): Promise<void> => {
        await apiClient.delete(`/api/v1/prm/projects/${projectId}/tasks/${id}`);
    },

    swapTaskSequence: async (
        projectId: string | number,
        taskId: string | number,
        targetSequenceNo: number,
    ): Promise<Task> => {
        const response = await apiClient.post(
            `/api/v1/prm/projects/${projectId}/tasks/${taskId}/swap-sequence`,
            { target_sequence_no: targetSequenceNo },
        );
        return response.data;
    },

    getDependencies: async (projectId: string | number): Promise<Dependency[]> => {
        const response = await apiClient.get(`/api/v1/prm/projects/${projectId}/dependencies`);
        return response.data;
    },

    createDependency: async (projectId: string | number, data: DependencyCreate): Promise<Dependency> => {
        const response = await apiClient.post(`/api/v1/prm/projects/${projectId}/dependencies`, data);
        return response.data;
    },

    deleteDependency: async (projectId: string | number, dependencyId: string | number): Promise<void> => {
        await apiClient.delete(`/api/v1/prm/projects/${projectId}/dependencies/${dependencyId}`);
    },

    getProjectSchedule: async (projectId: string | number): Promise<Task[]> => {
        const response = await apiClient.get(`/api/v1/prm/projects/${projectId}/schedule`);
        return response.data;
    },

    // --- Resources ---

    getTaskResources: async (taskId: number): Promise<TaskResource[]> => {
        const response = await apiClient.get(`/api/v1/prm/tasks/${taskId}/resources`);
        return response.data;
    },

    createTaskResource: async (taskId: number, data: TaskResourceCreate): Promise<TaskResource> => {
        const response = await apiClient.post(`/api/v1/prm/tasks/${taskId}/resources`, data);
        return response.data;
    },

    updateTaskResource: async (resourceId: number, data: TaskResourceUpdate): Promise<TaskResource> => {
        const response = await apiClient.put(`/api/v1/prm/resources/${resourceId}`, data);
        return response.data;
    },

    deleteTaskResource: async (resourceId: number): Promise<void> => {
        await apiClient.delete(`/api/v1/prm/resources/${resourceId}`);
    },

    // --- Resource Actuals ---
    getTaskResourceActuals: async (taskId: number): Promise<TaskResourceActual[]> => {
        const response = await apiClient.get(`/api/v1/prm/tasks/${taskId}/resource-actuals`);
        return response.data;
    },

    createTaskResourceActual: async (taskId: number, data: TaskResourceActualCreate): Promise<TaskResourceActual> => {
        const response = await apiClient.post(`/api/v1/prm/tasks/${taskId}/resource-actuals`, data);
        return response.data;
    },

    deleteTaskResourceActual: async (actualId: number): Promise<void> => {
        await apiClient.delete(`/api/v1/prm/resource-actuals/${actualId}`);
    },

    // --- Masters (Helpers) ---
    getMenMasters: async (): Promise<any[]> => {
        const response = await apiClient.get('/api/v1/cst/men-masters');
        return response.data;
    },

    getMachineMasters: async (): Promise<any[]> => {
        const response = await apiClient.get('/api/v1/cst/machine-masters');
        return response.data;
    }
};

export interface TaskResource {
    id: number;
    task_id: number;
    resource_type: 'STAFF' | 'MEN' | 'MACHINE' | 'MATERIAL';
    resource_id: number;
    resource_name?: string;
    quantity: number;
    effort_hours: number;
    rate: number;
    total_cost: number;
}

export interface TaskResourceCreate {
    resource_type: 'STAFF' | 'MEN' | 'MACHINE' | 'MATERIAL';
    resource_id: number;
    quantity: number;
    effort_hours: number;
}

export interface TaskResourceActual {
    id: number;
    task_id: number;
    resource_type: 'STAFF' | 'MEN' | 'MACHINE' | 'MATERIAL';
    resource_id: number;
    resource_name?: string;
    actual_date: string;
    actual_hours: number;
    rate: number;
    total_cost: number;
}

export interface TaskResourceActualCreate {
    resource_type: 'STAFF' | 'MEN' | 'MACHINE' | 'MATERIAL';
    resource_id: number;
    actual_date: string;
    actual_hours: number;
}
export interface TaskResourceUpdate {
    quantity?: number;
    effort_hours?: number;
    rate?: number;
}
